/**
 * OrgDocument lifecycle + preview-at-approval — Document Layer I7 SSOT
 *
 * @see .cursor/plans/archived/2026-q2/document-layer/i7-approval-lifecycle.md
 * @see .cursor/plans/_overview/DOCUMENT_LAYER_STANDARD.md v1.4 § 승인
 *
 * AdaptiveDecision Surface 연결 금지 — 사람 승인은 OrgDocument 레이어만.
 */

import type {
  ApprovalPreviewSnapshot,
  DocumentApprovalFlags,
  IntercompanyPreviewSnapshot,
  OrgDocumentLifecycle,
  OrgDocumentRecord,
} from './org-document-types';
import { DOCUMENT_APPROVE_PERMISSION } from '@shared/types/internal-entity-permission-matrix';
import {
  resolveLegalEntityApproverRouting,
  isLegalEntityApproverAuthorized,
  isTenantDefaultApprover,
  hasSubdomainApproverPermission,
  type LegalEntityApproverContext,
} from './legal-entity-approver-routing';
import type { UserRole } from '@shared/types/roles';

export type {
  ApprovalPreviewSnapshot,
  DocumentApprovalFlags,
  IntercompanyPreviewSnapshot,
  OrgDocumentLifecycle,
  OrgDocumentLifecycleRecord,
  OrgDocumentRecord,
} from './org-document-types';

export class DocumentNotApprovedError extends Error {
  readonly documentType: string;
  readonly lifecycle: OrgDocumentLifecycle;

  constructor(documentType: string, lifecycle: OrgDocumentLifecycle) {
    super(`문서 "${documentType}"는 승인 후에만 실행할 수 있습니다 (현재: ${lifecycle}).`);
    this.name = 'DocumentNotApprovedError';
    this.documentType = documentType;
    this.lifecycle = lifecycle;
  }
}

export class DocumentLifecycleError extends Error {
  readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = 'DocumentLifecycleError';
    this.code = code;
  }
}

export interface ApprovalPreviewInput {
  documentType: string;
  payload: Record<string, unknown>;
  flags?: DocumentApprovalFlags;
}

interface PreviewLineItem {
  side?: 'debit' | 'credit';
  amount?: number;
}

function parseLineItems(payload: Record<string, unknown>): PreviewLineItem[] {
  const raw = payload.lineItems;
  if (!Array.isArray(raw)) return [];
  return raw.filter((item): item is PreviewLineItem => typeof item === 'object' && item !== null);
}

function computeBalancePreview(lineItems: PreviewLineItem[]): {
  debit: number;
  credit: number;
  balanced: boolean;
} {
  const debit = lineItems
    .filter((line) => line.side === 'debit')
    .reduce((sum, line) => sum + (line.amount ?? 0), 0);
  const credit = lineItems
    .filter((line) => line.side === 'credit')
    .reduce((sum, line) => sum + (line.amount ?? 0), 0);
  return {
    debit,
    credit,
    balanced: Math.abs(debit - credit) <= 0.01,
  };
}

/**
 * preview-at-approval — execute와 동일 파이프 dry-run (I7 Step 2 stub).
 * FlowInputResolver · balanceRules · budget · policy는 payload 기반 preview.
 */
export function buildApprovalPreview(input: ApprovalPreviewInput): ApprovalPreviewSnapshot {
  const lineItems = parseLineItems(input.payload);
  const snapshot: ApprovalPreviewSnapshot = {
    computedAt: new Date().toISOString(),
    flowInputResolved: {
      documentType: input.documentType,
      context: input.payload.context ?? {},
      lineItems,
    },
  };

  if (lineItems.length > 0) {
    const balancePreview = computeBalancePreview(lineItems);
    snapshot.balancePreview = balancePreview;
    if (!balancePreview.balanced) {
      snapshot.policyViolations = [
        ...(snapshot.policyViolations ?? []),
        { code: 'BALANCE_MISMATCH', messageKo: '차변과 대변 합계가 일치하지 않습니다.' },
      ];
    }
  }

  if (input.flags?.requiresBudgetCheck) {
    const budgetId =
      typeof input.payload.budgetId === 'string' ? input.payload.budgetId : 'finance_budget';
    const remaining =
      typeof input.payload.budgetRemaining === 'number' ? input.payload.budgetRemaining : null;
    const totalAmount = lineItems.reduce((sum, line) => sum + (line.amount ?? 0), 0) / 2;
    const wouldExceed = remaining != null && totalAmount > remaining;
    snapshot.budgetPreview = {
      budgetId,
      remaining: remaining ?? 0,
      wouldExceed,
    };
    if (wouldExceed) {
      snapshot.policyViolations = [
        ...(snapshot.policyViolations ?? []),
        { code: 'BUDGET_EXCEEDED', messageKo: '예산 잔액을 초과합니다.' },
      ];
    }
  }

  appendLegalEntityApprovalPreview(snapshot, input);
  appendKnowledgeWorkflowPreview(snapshot, input);

  return snapshot;
}

interface IntercompanyPairFields {
  fromLegalEntityId?: string;
  toLegalEntityId?: string;
}

export function readContextFieldFromPayload(
  payload: Record<string, unknown>,
  key: string
): string | undefined {
  return readContextField(payload, key);
}

export function readIntercompanyPairFromPayload(
  payload: Record<string, unknown>
): IntercompanyPairFields {
  return readIntercompanyPair(payload);
}

function readContextField(payload: Record<string, unknown>, key: string): string | undefined {
  if (typeof payload[key] === 'string' && payload[key].trim()) {
    return payload[key].trim();
  }

  const contexts = [payload.context, payload.contextSelection].filter(
    (value): value is Record<string, unknown> =>
      typeof value === 'object' && value !== null && !Array.isArray(value)
  );

  for (const context of contexts) {
    const raw = context[key];
    if (typeof raw === 'string' && raw.trim()) return raw.trim();
    if (Array.isArray(raw) && typeof raw[0] === 'string' && raw[0].trim()) return raw[0].trim();
  }

  return undefined;
}

interface IntercompanyPairFields {
  fromLegalEntityId?: string;
  toLegalEntityId?: string;
}

function readIntercompanyPair(payload: Record<string, unknown>): IntercompanyPairFields {
  const pairRaw = payload.intercompanyPair;
  if (typeof pairRaw === 'object' && pairRaw !== null && !Array.isArray(pairRaw)) {
    const pair = pairRaw as Record<string, unknown>;
    const from = readContextField({ context: pair }, 'fromLegalEntityId');
    const to = readContextField({ context: pair }, 'toLegalEntityId');
    if (from || to) {
      return {
        ...(from ? { fromLegalEntityId: from } : {}),
        ...(to ? { toLegalEntityId: to } : {}),
      };
    }
  }

  const intercompanyRaw = payload.intercompany;
  if (
    typeof intercompanyRaw === 'object' &&
    intercompanyRaw !== null &&
    !Array.isArray(intercompanyRaw)
  ) {
    const intercompany = intercompanyRaw as Record<string, unknown>;
    const from = readContextField({ context: intercompany }, 'fromLegalEntityId');
    const to = readContextField({ context: intercompany }, 'toLegalEntityId');
    if (from || to) {
      return {
        ...(from ? { fromLegalEntityId: from } : {}),
        ...(to ? { toLegalEntityId: to } : {}),
      };
    }
  }

  const from = readContextField(payload, 'fromLegalEntityId');
  const to = readContextField(payload, 'toLegalEntityId');
  return {
    ...(from ? { fromLegalEntityId: from } : {}),
    ...(to ? { toLegalEntityId: to } : {}),
  };
}

function resolveCounterpartyLegalEntityId(
  legalEntityId: string | undefined,
  pair: IntercompanyPairFields
): string | null {
  const { fromLegalEntityId, toLegalEntityId } = pair;
  if (!legalEntityId || !fromLegalEntityId || !toLegalEntityId) return null;
  if (legalEntityId === fromLegalEntityId) return toLegalEntityId;
  if (legalEntityId === toLegalEntityId) return fromLegalEntityId;
  return null;
}

function appendIntercompanyPairViolations(
  snapshot: ApprovalPreviewSnapshot,
  legalEntityId: string | undefined,
  pair: IntercompanyPairFields
): void {
  const { fromLegalEntityId, toLegalEntityId } = pair;
  if (!fromLegalEntityId || !toLegalEntityId) return;

  if (fromLegalEntityId === toLegalEntityId) {
    snapshot.policyViolations = [
      ...(snapshot.policyViolations ?? []),
      {
        code: 'INTERCOMPANY_PAIR_INVALID',
        messageKo: '내부거래 법인 쌍은 서로 다른 법인이어야 합니다.',
      },
    ];
    return;
  }

  if (legalEntityId && legalEntityId !== fromLegalEntityId && legalEntityId !== toLegalEntityId) {
    snapshot.policyViolations = [
      ...(snapshot.policyViolations ?? []),
      {
        code: 'INTERCOMPANY_PAIR_MISMATCH',
        messageKo: '선택한 법인(legalEntityId)이 내부거래 쌍(from/to)에 포함되지 않습니다.',
      },
    ];
  }
}

function appendLegalEntityApprovalPreview(
  snapshot: ApprovalPreviewSnapshot,
  input: ApprovalPreviewInput
): void {
  if (!input.flags?.requiresLegalEntity) return;

  const legalEntityId = readContextField(input.payload, 'legalEntityId');

  snapshot.flowInputResolved = {
    ...snapshot.flowInputResolved,
    legalEntityPreview: {
      legalEntityId: legalEntityId ?? null,
      approverRouting: resolveLegalEntityApproverRouting(true, input.documentType, undefined),
    },
  };

  if (!legalEntityId) {
    snapshot.policyViolations = [
      ...(snapshot.policyViolations ?? []),
      {
        code: 'LEGAL_ENTITY_REQUIRED',
        messageKo: '법인(legalEntityId)을 선택해야 결재 요청할 수 있습니다.',
      },
    ];
  }

  if (input.documentType === 'intercompany_elimination') {
    const intercompanyId = readContextField(input.payload, 'intercompanyId');
    const pair = readIntercompanyPair(input.payload);
    const counterpartyLegalEntityId = resolveCounterpartyLegalEntityId(legalEntityId, pair);

    const intercompanyPreview: IntercompanyPreviewSnapshot = {
      intercompanyId: intercompanyId ?? null,
      messageKo:
        '내부거래 상쇄 preview — 선택 법인·거래 쌍·FlowInputResolver 다법인 dry-run 스냅샷.',
    };
    if (pair.fromLegalEntityId) {
      intercompanyPreview.fromLegalEntityId = pair.fromLegalEntityId;
    }
    if (pair.toLegalEntityId) {
      intercompanyPreview.toLegalEntityId = pair.toLegalEntityId;
    }
    if (counterpartyLegalEntityId) {
      intercompanyPreview.counterpartyLegalEntityId = counterpartyLegalEntityId;
    }

    snapshot.flowInputResolved = {
      ...snapshot.flowInputResolved,
      intercompanyPreview,
    };

    if (!intercompanyId) {
      snapshot.policyViolations = [
        ...(snapshot.policyViolations ?? []),
        {
          code: 'INTERCOMPANY_REQUIRED',
          messageKo: '내부거래(intercompanyId)를 선택해야 결재 요청할 수 있습니다.',
        },
      ];
    }

    appendIntercompanyPairViolations(snapshot, legalEntityId, pair);
  }
}

/** HTTP status for DocumentLifecycleError codes (fail-loud). */
export function documentLifecycleErrorHttpStatus(code: string): number {
  if (code === 'NOT_FOUND') return 404;
  if (
    code === 'APPROVER_UNAUTHORIZED' ||
    code === 'APPROVER_ROLE_REQUIRED' ||
    code === 'APPROVER_CONTEXT_REQUIRED'
  ) {
    return 403;
  }
  return 422;
}

export interface OrgDocumentApproverGateInput {
  approverRole?: UserRole;
  approverPermissions?: readonly string[];
  requiresLegalEntity: boolean;
  documentType: string;
  primarySubdomain?: string;
}

/** Approve/reject gate — legal-entity: subdomain permission matrix; otherwise document:approve or tenant fallback. */
export function assertOrgDocumentApproverAuthorized(input: OrgDocumentApproverGateInput): void {
  if (input.requiresLegalEntity) {
    assertLegalEntityApproverAuthorized({
      documentType: input.documentType,
      ...(input.approverRole !== undefined ? { role: input.approverRole } : {}),
      ...(input.approverPermissions !== undefined
        ? { permissions: input.approverPermissions }
        : {}),
      ...(input.primarySubdomain !== undefined ? { primarySubdomain: input.primarySubdomain } : {}),
    });
    return;
  }

  if (isTenantDefaultApprover(input.approverRole)) return;
  if (hasSubdomainApproverPermission(input.approverPermissions, [DOCUMENT_APPROVE_PERMISSION])) {
    return;
  }

  if (
    !input.approverRole &&
    (!input.approverPermissions || input.approverPermissions.length === 0)
  ) {
    throw new DocumentLifecycleError(
      'APPROVER_CONTEXT_REQUIRED',
      '결재자 인증 정보가 필요합니다. JWT 로그인 후 document:approve 권한 또는 결재자 역할이 필요합니다.'
    );
  }

  throw new DocumentLifecycleError(
    'APPROVER_UNAUTHORIZED',
    '결재 권한이 없습니다. document:approve 권한 또는 테넌트 소유자·운영 관리자 역할이 필요합니다.'
  );
}

/** Fail-loud gate for approve/reject on legal-entity documents (subdomain permission + tenant fallback). */
export function assertLegalEntityApproverAuthorized(ctx: LegalEntityApproverContext): void {
  const routing = resolveLegalEntityApproverRouting(true, ctx.documentType, ctx.primarySubdomain);

  if (isLegalEntityApproverAuthorized(ctx, routing)) return;

  const required = routing?.requiredPermissions.join(', ') ?? DOCUMENT_APPROVE_PERMISSION;
  const roleLabel = ctx.role ?? '(없음)';
  const permLabel =
    ctx.permissions && ctx.permissions.length > 0 ? ctx.permissions.join(', ') : '(없음)';

  if (!ctx.role && (!ctx.permissions || ctx.permissions.length === 0)) {
    throw new DocumentLifecycleError(
      'APPROVER_CONTEXT_REQUIRED',
      '결재자 역할(role) 또는 JWT subdomain 권한(permissions)이 필요합니다. Internal Entity 매트릭스에서 document:approve 또는 posting-family approve 권한을 위임하세요.'
    );
  }

  throw new DocumentLifecycleError(
    'APPROVER_UNAUTHORIZED',
    `결재 권한이 없습니다 (role: ${roleLabel}, permissions: ${permLabel}). 필요 권한: ${required} — 또는 테넌트 소유자/운영 관리자.`
  );
}

/** @deprecated Use assertLegalEntityApproverAuthorized — role-only tenant default gate. */
export function assertTenantDefaultApproverRole(role: UserRole | undefined): void {
  assertLegalEntityApproverAuthorized({
    documentType: 'legal_entity_opening',
    permissions: [],
    ...(role !== undefined ? { role } : {}),
  });
}

/** Fail-loud gate — blocks request-approval when legal-entity / intercompany policy is violated. */
export function assertApprovalPreviewPolicy(preview: ApprovalPreviewSnapshot): void {
  const blockingCodes = new Set([
    'LEGAL_ENTITY_REQUIRED',
    'INTERCOMPANY_REQUIRED',
    'INTERCOMPANY_PAIR_INVALID',
    'INTERCOMPANY_PAIR_MISMATCH',
  ]);
  const violation = preview.policyViolations?.find((item) => blockingCodes.has(item.code));
  if (violation) {
    throw new DocumentLifecycleError(violation.code, violation.messageKo);
  }
}

function appendKnowledgeWorkflowPreview(
  snapshot: ApprovalPreviewSnapshot,
  input: ApprovalPreviewInput
): void {
  const { documentType, payload } = input;

  if (documentType === 'knowledge_policy') {
    const rules = Array.isArray(payload.rules) ? payload.rules : [];
    const contentBlocks = Array.isArray(payload.contentBlocks)
      ? payload.contentBlocks
      : payload.body
        ? [{ type: 'body' }]
        : [];
    snapshot.flowInputResolved = {
      ...snapshot.flowInputResolved,
      knowledgeImpactPreview: {
        kind: 'policy',
        ruleCount: rules.length,
        blockCount: contentBlocks.length,
      },
    };
    if (rules.length === 0) {
      snapshot.policyViolations = [
        ...(snapshot.policyViolations ?? []),
        { code: 'POLICY_RULES_EMPTY', messageKo: '정책 규칙이 하나 이상 필요합니다.' },
      ];
    }
    return;
  }

  if (documentType === 'knowledge_plan') {
    const connections = Array.isArray(payload.draftFlowConnections)
      ? payload.draftFlowConnections
      : [];
    snapshot.flowInputResolved = {
      ...snapshot.flowInputResolved,
      knowledgeImpactPreview: {
        kind: 'plan',
        flowCount: connections.length,
      },
    };
    if (connections.length === 0) {
      snapshot.policyViolations = [
        ...(snapshot.policyViolations ?? []),
        { code: 'PLAN_FLOWS_EMPTY', messageKo: '계획에 Flow가 하나 이상 정의되어야 합니다.' },
      ];
    }
  }
}

export function requiresApprovalFlag(meta: { flags?: DocumentApprovalFlags }): boolean {
  return meta.flags?.requiresApproval === true;
}

/** execute / publish 직전 lifecycle gate */
export function assertExecutable(
  record: OrgDocumentRecord,
  meta: { documentType: string; flags?: DocumentApprovalFlags }
): void {
  if (!requiresApprovalFlag(meta)) return;
  if (record.lifecycle !== 'approved') {
    throw new DocumentNotApprovedError(record.documentType, record.lifecycle);
  }
}

export function canRequestApproval(
  lifecycle: OrgDocumentLifecycle,
  requiresApproval: boolean
): boolean {
  return requiresApproval && (lifecycle === 'draft' || lifecycle === 'rejected');
}

export function canApprove(lifecycle: OrgDocumentLifecycle, isApprover: boolean): boolean {
  return isApprover && lifecycle === 'in_review';
}

export function canReject(lifecycle: OrgDocumentLifecycle, isApprover: boolean): boolean {
  return isApprover && lifecycle === 'in_review';
}

export function canExecuteAfterApproval(
  lifecycle: OrgDocumentLifecycle,
  requiresApproval: boolean
): boolean {
  if (!requiresApproval) return lifecycle === 'draft' || lifecycle === 'rejected';
  return lifecycle === 'approved';
}

export function transitionRequestApproval(
  record: OrgDocumentRecord,
  preview: ApprovalPreviewSnapshot
): OrgDocumentRecord {
  if (!canRequestApproval(record.lifecycle, true)) {
    throw new DocumentLifecycleError(
      'INVALID_TRANSITION',
      `결재 요청 불가 lifecycle: ${record.lifecycle}`
    );
  }
  return {
    ...record,
    lifecycle: 'in_review',
    approvalPreview: preview,
  };
}

export function transitionApprove(record: OrgDocumentRecord): OrgDocumentRecord {
  if (record.lifecycle !== 'in_review') {
    throw new DocumentLifecycleError(
      'INVALID_TRANSITION',
      `승인 불가 lifecycle: ${record.lifecycle}`
    );
  }
  if (!record.approvalPreview) {
    throw new DocumentLifecycleError('PREVIEW_REQUIRED', '승인 preview 스냅샷이 없습니다.');
  }
  return { ...record, lifecycle: 'approved' };
}

export function transitionReject(record: OrgDocumentRecord): OrgDocumentRecord {
  if (record.lifecycle !== 'in_review') {
    throw new DocumentLifecycleError(
      'INVALID_TRANSITION',
      `반려 불가 lifecycle: ${record.lifecycle}`
    );
  }
  const { approvalPreview: _removed, ...rest } = record;
  return { ...rest, lifecycle: 'rejected' };
}

export function transitionPosted(record: OrgDocumentRecord): OrgDocumentRecord {
  return { ...record, lifecycle: 'posted' };
}

export function createDraftRecord(documentType: string): OrgDocumentRecord {
  return { documentType, lifecycle: 'draft' };
}
